import { useEffect, useMemo, useState } from 'react'
import {
	Input,
	FilterPill,
	IconButton,
	Label,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
	DotMatrix,
	Pagination
} from '@clickguard/design-system'
import { AccountLinkDTO } from 'api-models'
import { useAccountStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import SearchSm from '@untitled-ui/icons-react/build/esm/SearchSm'
import Settings01 from '@untitled-ui/icons-react/build/esm/Settings01'
import AlertCircle from '@untitled-ui/icons-react/build/esm/AlertCircle'
import { GEmpty } from 'components/basic-blocks/g-empty'
import {
	ConnectionsFilters,
	CREDENTIAL_FILTER_GROUPS,
	ConnectionsFilterState
} from './connections-filters'
import {
	CredentialIcon,
	credentialEmail,
	credentialHasIssue,
	credentialLabel,
	getCredentialStatus
} from './credential-helpers'
import { CredentialDrawer } from './credential-drawer'
import { SortHeadCell, SortState, nextSortState } from './sort-head-cell'
import { idCollator } from './connection-helpers'

export const CredentialsTab = () => {
	const user = useUserStore((s) => s.user)
	const { accountLinks, refreshAccountLinks, account, userAccess } =
		useAccountStore(
			useShallow((s) => ({
				accountLinks: s.accountLinks,
				refreshAccountLinks: s.refreshAccountLinks,
				account: s.account,
				userAccess: s.userAccess
			}))
		)

	const [loading, setLoading] = useState(true)
	const [search, setSearch] = useState('')
	const [filters, setFilters] = useState<ConnectionsFilterState>({})
	const [pendingOnly, setPendingOnly] = useState(false)
	const [selected, setSelected] = useState<AccountLinkDTO | null>(null)
	const [drawerOpen, setDrawerOpen] = useState(false)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	// §2.7 — Credential and Connection status are both sortable.
	const [sort, setSort] = useState<SortState>({ key: 'credential', dir: 'asc' })

	const isAnalyst = userAccess(user?.id || '') === 'READ'

	useEffect(() => {
		let cancelled = false
		;(async () => {
			setLoading(true)
			try {
				await refreshAccountLinks()
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [account?.id])

	const { rows, pendingCount } = useMemo(() => {
		// GTM is not a manageable credential and is excluded from this table.
		let list: AccountLinkDTO[] = (accountLinks ?? []).filter(
			(l) => l.platform !== 'GTM'
		)
		const q = search.trim().toLowerCase()
		if (q)
			list = list.filter(
				(l) =>
					credentialLabel(l).toLowerCase().includes(q) ||
					credentialEmail(l).toLowerCase().includes(q)
			)
		if (filters.platforms?.length)
			list = list.filter((l) => filters.platforms.includes(l.platform))
		const pendingCount = list.filter(credentialHasIssue).length
		const rows = pendingOnly ? list.filter(credentialHasIssue) : list
		return { rows, pendingCount }
	}, [accountLinks, search, filters, pendingOnly])

	const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))

	// Keep the current page in range as filters/search shrink the result set.
	useEffect(() => {
		if (page > totalPages) setPage(totalPages)
	}, [page, totalPages])

	const sortedRows = useMemo(() => {
		if (!sort) return rows
		const value = (row: AccountLinkDTO) =>
			sort.key === 'connectionStatus'
				? getCredentialStatus(row).text
				: credentialLabel(row)
		return [...rows].sort(
			(a, b) =>
				idCollator.compare(value(a), value(b)) * (sort.dir === 'desc' ? -1 : 1)
		)
	}, [rows, sort])

	const pagedRows = useMemo(
		() => sortedRows.slice((page - 1) * pageSize, page * pageSize),
		[sortedRows, page, pageSize]
	)

	const openDrawer = (link: AccountLinkDTO) => {
		setSelected(link)
		setDrawerOpen(true)
	}

	const columns: Array<{
		id: string
		header: string
		align?: 'right'
		sortable?: boolean
		cell: (row: AccountLinkDTO) => JSX.Element
	}> = [
		{
			id: 'credential',
			header: 'Credential',
			sortable: true,
			cell: (row) => (
				<div className="flex items-center gap-3">
					<CredentialIcon link={row} />
					<div className="flex flex-col">
						<span className="text-t-default text-sm">
							{credentialLabel(row)}
						</span>
						<span className="text-t-secondary text-xs truncate max-w-[260px]">
							{credentialEmail(row)}
						</span>
					</div>
				</div>
			)
		},
		{
			id: 'connectionStatus',
			header: 'Connection status',
			sortable: true,
			cell: (row) => {
				const status = getCredentialStatus(row)
				return (
					<Label size="md" color={status.color}>
						{status.text}
					</Label>
				)
			}
		},
		{
			id: 'actions',
			header: '',
			align: 'right',
			cell: (row) => (
				<div className="flex items-center justify-end gap-2">
					{credentialHasIssue(row) && (
						<AlertCircle className="h-5 w-5 text-red-500 shrink-0" />
					)}
					<IconButton
						size="sm"
						color="gray"
						aria-label="Credentials settings"
						icon={<Settings01 className="h-4 w-4" />}
						onClick={() => openDrawer(row)}
					/>
				</div>
			)
		}
	]

	return (
		<div className="flex flex-col">
			<div className="flex flex-col gap-4 pt-4">
				<div className="flex items-center gap-[12px]">
					<Input
						state="default"
						size="sm"
						placeholder="Search for credentials..."
						leadingIcon={<SearchSm />}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					<ConnectionsFilters
						groups={CREDENTIAL_FILTER_GROUPS}
						value={filters}
						onChange={setFilters}
					/>
					{pendingCount > 0 && (
						<FilterPill
							label="Pending issues"
							count={pendingCount}
							pressed={pendingOnly}
							onPressedChange={setPendingOnly}
						/>
					)}
				</div>

				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								{columns.map((col) =>
									col.sortable ? (
										<SortHeadCell
											key={col.id}
											label={col.header}
											sortKey={col.id}
											sort={sort}
											onSort={(key) => setSort(nextSortState(sort, key))}
											align={col.align}
										/>
									) : (
										<TableHead
											key={col.id}
											className={
												col.align === 'right' ? 'text-right' : undefined
											}
										>
											{col.header}
										</TableHead>
									)
								)}
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<div className="flex justify-center py-16">
											<DotMatrix label="Loading credentials" />
										</div>
									</TableCell>
								</TableRow>
							) : rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length}>
										<GEmpty
											title="No credentials"
											description="Connect an ad account to add a credential."
										/>
									</TableCell>
								</TableRow>
							) : (
								pagedRows.map((row) => (
									<TableRow key={row.id}>
										{columns.map((col) => (
											<TableCell
												key={col.id}
												className={
													col.align === 'right' ? 'text-right' : undefined
												}
											>
												{col.cell(row)}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>
			</div>
			{!loading && rows.length > 0 && (
				<Pagination
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					pageSize={pageSize}
					pageSizeOptions={[10, 20, 50]}
					onPageSizeChange={(size) => {
						setPageSize(size)
						setPage(1)
					}}
				/>
			)}

			<CredentialDrawer
				link={selected}
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				isAnalyst={isAnalyst}
			/>
		</div>
	)
}
